/**
 * Shared prop vocabulary for the design system.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  THE PROP NAMING RULES
 * ─────────────────────────────────────────────────────────────────────
 *
 *  `size`      How big. Always the {@link ControlSize} scale, always the
 *              same heights (see below). Never `inputSize`, never `scale`.
 *
 *  `variant`   How much *emphasis* / what shape. Button only. Values are
 *              about visual weight (`primary`, `ghost`, `outline`), not
 *              about meaning.
 *
 *  `tone`      What it *means*. A {@link Tone} from `lib/colors` — the one
 *              place that decides a loss is red. Badge, AlertCard,
 *              ConfirmModal, StatRow, Toast.
 *
 *  `icon`      An {@link IconComponent}, rendered leading. `iconAfter` for
 *              trailing. Never a rendered `<Icon />` element — passing the
 *              component lets the container size it to match its own scale.
 *
 *  `className` Always last in the merge, always applied to the outermost
 *              element, always present.
 *
 *  `onChange`  Receives the *value*, not the DOM event, on every wrapper
 *              component (Select, Toggle, Checkbox, Tabs, ButtonGroup). Raw
 *              `<input>` passthroughs (Input, Textarea) keep the event,
 *              because they are the native element.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  THE SIZE SCALE
 * ─────────────────────────────────────────────────────────────────────
 *
 *      xs   h-6 / 24px    dense toolbars, table-cell controls
 *      sm   h-7 / 28px    the terminal default — filter bars, top bar
 *      md   h-8 / 32px    forms
 *      lg   h-10 / 40px   the one primary action on a page
 *
 * These heights are shared by Button, Input and Select so that a filter row
 * of mixed controls lines up. A component that supports only part of the
 * scale narrows the union rather than inventing new names for the steps.
 */

import type { ComponentType } from 'react';

export type ControlSize = 'xs' | 'sm' | 'md' | 'lg';

/** The subset every text-entry control supports. */
export type FieldSize = Extract<ControlSize, 'xs' | 'sm' | 'md'>;

/** The subset badges and pills support. */
export type BadgeSize = Extract<ControlSize, 'xs' | 'sm'>;

/**
 * A lucide icon, passed as a component.
 *
 * Deliberately structural rather than `LucideIcon`: it also admits a plain
 * SVG wrapper of our own, and it keeps the design system from depending on
 * a specific icon package's type in its public surface.
 */
export type IconComponent = ComponentType<{
  size?: number | string;
  className?: string;
  strokeWidth?: number | string;
}>;

/** Shared height classes, so three files cannot drift to three heights. */
export const CONTROL_HEIGHT: Record<ControlSize, string> = {
  xs: 'h-11 md:h-6',
  sm: 'h-11 md:h-7',
  md: 'h-11 md:h-8',
  lg: 'h-11 md:h-10',
};

/**
 * Text-field heights, matching {@link CONTROL_HEIGHT}'s so a filter row of
 * mixed controls lines up. Lives here rather than in `Input.tsx` so that file
 * can export only components (Fast Refresh), and so `Select` can read it
 * without importing from a component module.
 */
export const FIELD_SIZES: Record<FieldSize, string> = {
  xs: 'h-11 text-[11px] md:h-6',
  sm: 'h-11 text-xs md:h-7',
  md: 'h-11 text-[13px] md:h-8',
};
